/**
 * Global dictation state — Zustand port of the SvelteKit stores.
 *
 * Mirrors the Svelte stores:
 *   web/src/lib/stores/dictation.ts
 *
 * Zustand's shallow-equality hook selectors give us a close
 * feel to Svelte's `$store` reactivity.
 */

import { create } from "zustand";
import type { ClinicalAnalysisResult } from "@/types/api";

interface ToastState {
  id: number;
  message: string;
  kind: "success" | "error" | "info";
  durationMs: number;
}

interface DictationStoreState {
  analysisResult: ClinicalAnalysisResult | null;

  toasts: ToastState[];

  // Actions
  setAnalysisResult: (result: ClinicalAnalysisResult | null) => void;

  showToast: (message: string, kind?: ToastState["kind"], durationMs?: number) => void;
  dismissToast: (id: number) => void;
}

let toastIdCounter = 0;

export const useDictationStore = create<DictationStoreState>()((set) => ({
  analysisResult: null,
  toasts: [],

  setAnalysisResult: (result) => set({ analysisResult: result }),

  showToast: (message, kind = "info", durationMs = 2500) => {
    const id = ++toastIdCounter;
    set((state) => ({
      toasts: [...state.toasts, { id, message, kind, durationMs }],
    }));
    setTimeout(() => {
      set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
    }, durationMs);
  },

  dismissToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));
